import { useEffect, useRef, useState } from 'react';

import { POLLING_INTERVAL_MS, POLLING_MAX_DURATION_MS } from './pollingConstants';

/**
 * 캐시된 리포트가 있고 재분석이 진행 중일 때, 업데이트 가능 여부를 polling 하는 훅
 *
 * - react-query 캐시를 갱신하지 않도록 raw API 함수를 직접 호출한다.
 *   (완료되면 사용자가 "업데이트" 버튼으로 직접 refetch 하도록 하기 위함)
 * - pollingCondition 이 false 가 되거나 언마운트되면 즉시 정리한다.
 * - 실패 또는 최대 지속 시간 초과 시 조용히 중단하고 needUpdate 를 false 로 두어 버튼을 숨긴다.
 *   pollingCondition 이 그대로 true 인 동안에는 다시 시작하지 않는다.
 * - sessionKey 가 바뀌면 (예: 다른 인플루언서 리포트로 전환) 조건이 그대로여도 새 세션을 시작한다.
 *
 * @param pollingCondition polling 조건
 * @param getReportFunc 리포트를 가져오는 함수 (raw API, 실패 시 reject)
 * @param sessionKey polling 대상을 식별하는 키. 바뀌면 진행 중인 세션을 정리하고 새로 시작한다.
 * @returns {needUpdate, isLoading, readyForUpdate}
 */
const usePollingForUpdate = (
	pollingCondition: boolean,
	getReportFunc: () => Promise<any>,
	sessionKey?: string,
) => {
	// 매 렌더마다 최신 함수를 참조해 interval 콜백이 stale closure 를 잡지 않도록 한다.
	const getReportFuncRef = useRef(getReportFunc);
	getReportFuncRef.current = getReportFunc;

	// 완료 없이 종료된 뒤 pollingCondition 이 그대로일 때 재시작을 막는 가드
	const hasSettledRef = useRef(false);
	const lastSessionKeyRef = useRef(sessionKey);

	const [needUpdate, setNeedUpdate] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [readyForUpdate, setReadyForUpdate] = useState(false);

	useEffect(() => {
		// 대상이 바뀌면 이전 세션의 종료 상태를 물려받지 않는다.
		if (lastSessionKeyRef.current !== sessionKey) {
			lastSessionKeyRef.current = sessionKey;
			hasSettledRef.current = false;
		}

		if (!pollingCondition) {
			hasSettledRef.current = false;
			setNeedUpdate(false);
			setIsLoading(false);
			setReadyForUpdate(false);
			return;
		}

		if (hasSettledRef.current) return;

		let isActive = true;
		let intervalId: ReturnType<typeof setInterval> | null = null;
		let timeoutId: ReturnType<typeof setTimeout> | null = null;

		const stop = () => {
			isActive = false;
			if (intervalId !== null) clearInterval(intervalId);
			if (timeoutId !== null) clearTimeout(timeoutId);
			intervalId = null;
			timeoutId = null;
		};

		const settle = (isCompleted: boolean) => {
			if (!isActive) return;
			stop();
			hasSettledRef.current = true;
			setIsLoading(false);

			if (isCompleted) {
				setReadyForUpdate(true);
			} else {
				setNeedUpdate(false);
			}
		};

		setNeedUpdate(true);
		setIsLoading(true);
		setReadyForUpdate(false);

		intervalId = setInterval(async () => {
			try {
				const res = await getReportFuncRef.current();
				if (!isActive) return;

				if (res?.is_completed) {
					settle(true);
				}
			} catch {
				settle(false);
			}
		}, POLLING_INTERVAL_MS);

		timeoutId = setTimeout(() => settle(false), POLLING_MAX_DURATION_MS);

		return stop;
	}, [pollingCondition, sessionKey]);

	return { needUpdate, isLoading, readyForUpdate };
};

export default usePollingForUpdate;
